import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ComplexNumber {
  real: number;
  imaginary: number;
}

const ComplexMultiplication = () => {
  const [z1, setZ1] = useState<ComplexNumber>({ real: 3, imaginary: 2 });
  const [z2, setZ2] = useState<ComplexNumber>({ real: 1, imaginary: 4 });
  const [inputValues, setInputValues] = useState({
    z1Real: '3',
    z1Imaginary: '2',
    z2Real: '1',
    z2Imaginary: '4'
  });
  const [result, setResult] = useState<ComplexNumber | null>(null);
  const [showSteps, setShowSteps] = useState(true);
  const { toast } = useToast();

  const multiplyComplex = (a: ComplexNumber, b: ComplexNumber): ComplexNumber => {
    // (a + bi) × (c + di) = (ac - bd) + (ad + bc)i
    return {
      real: a.real * b.real - a.imaginary * b.imaginary,
      imaginary: a.real * b.imaginary + a.imaginary * b.real
    };
  };

  const calculate = () => {
    // Update z1 and z2 with the latest input values
    const updatedZ1 = {
      real: inputValues.z1Real === '' ? 0 : parseInt(inputValues.z1Real, 10) || 0,
      imaginary: inputValues.z1Imaginary === '' ? 0 : parseInt(inputValues.z1Imaginary, 10) || 0
    };
    
    const updatedZ2 = {
      real: inputValues.z2Real === '' ? 0 : parseInt(inputValues.z2Real, 10) || 0,
      imaginary: inputValues.z2Imaginary === '' ? 0 : parseInt(inputValues.z2Imaginary, 10) || 0
    };
    
    setZ1(updatedZ1);
    setZ2(updatedZ2);
    
    const result = multiplyComplex(updatedZ1, updatedZ2);
    setResult(result);
    setShowSteps(true);
  };

  const formatComplex = (z: ComplexNumber, showParentheses = false) => {
    const realPart = z.real;
    const imagPart = z.imaginary;
    
    if (imagPart === 0) return realPart.toString();
    if (realPart === 0) return `${imagPart}i`;
    
    const sign = imagPart >= 0 ? '+' : '-';
    const imagDisplay = Math.abs(imagPart) === 1 ? 'i' : `${Math.abs(imagPart)}i`;
    const complexStr = `${realPart} ${sign} ${imagDisplay}`;
    
    return showParentheses ? `(${complexStr})` : complexStr;
  };

  const toPolar = (z: ComplexNumber) => {
    const magnitude = Math.sqrt(z.real * z.real + z.imaginary * z.imaginary);
    const angle = Math.atan2(z.imaginary, z.real);
    const angleDegrees = (angle * 180) / Math.PI;
    
    return {
      magnitude: magnitude,
      angle: angle,
      angleDegrees: angleDegrees
    };
  };

  const multiplyPolar = () => {
    const polar1 = toPolar(z1);
    const polar2 = toPolar(z2);
    
    return {
      magnitude: polar1.magnitude * polar2.magnitude,
      angle: polar1.angle + polar2.angle,
      angleDegrees: polar1.angleDegrees + polar2.angleDegrees
    };
  };

  // Calculate polar result whenever z1 or z2 changes
  const polarResult = useMemo(() => {
    if (!result) return null;
    return multiplyPolar();
  }, [z1, z2, result]);

  const copyResult = async () => {
    if (!result) return;
    
    const resultText = `Hasil Perkalian Bilangan Kompleks:
z₁ × z₂ = ${formatComplex(result)}

Langkah-langkah:
z₁ × z₂ = ${formatComplex(z1, true)} × ${formatComplex(z2, true)}
= (${z1.real} + ${z1.imaginary}i) × (${z2.real} + ${z2.imaginary}i)
= ${z1.real} × ${z2.real} + ${z1.real} × ${z2.imaginary}i + ${z1.imaginary}i × ${z2.real} + ${z1.imaginary}i × ${z2.imaginary}i
= ${z1.real * z2.real} + ${z1.real * z2.imaginary}i + ${z1.imaginary * z2.real}i + ${z1.imaginary * z2.imaginary}i²
= ${z1.real * z2.real} + ${z1.real * z2.imaginary}i + ${z1.imaginary * z2.real}i + ${z1.imaginary * z2.imaginary}(-1)
= ${z1.real * z2.real} + ${z1.real * z2.imaginary}i + ${z1.imaginary * z2.real}i - ${z1.imaginary * z2.imaginary}
= (${z1.real * z2.real} - ${z1.imaginary * z2.imaginary}) + (${z1.real * z2.imaginary} + ${z1.imaginary * z2.real})i
= ${result.real} + ${result.imaginary}i`;
    
    try {
      await navigator.clipboard.writeText(resultText);
      toast({
        title: "Berhasil!",
        description: "Hasil perhitungan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin hasil",
        variant: "destructive",
      });
    }
  };

  const copyPolarResult = async () => {
    if (!result || !polarResult) return;
    
    const polar1 = toPolar(z1);
    const polar2 = toPolar(z2);
    
    const polarText = `Hasil Perkalian dalam Bentuk Polar:

z₁ = ${polar1.magnitude.toFixed(3)} ∠ ${polar1.angleDegrees.toFixed(1)}°
z₂ = ${polar2.magnitude.toFixed(3)} ∠ ${polar2.angleDegrees.toFixed(1)}°

Rumus: z₁ × z₂ = (r₁ × r₂) ∠ (θ₁ + θ₂)

Hasil:
r = r₁ × r₂ = ${polarResult.magnitude.toFixed(3)}
θ = θ₁ + θ₂ = ${polarResult.angleDegrees.toFixed(1)}°

z₁ × z₂ = ${polarResult.magnitude.toFixed(3)} ∠ ${polarResult.angleDegrees.toFixed(1)}°`;
    
    try {
      await navigator.clipboard.writeText(polarText);
      toast({
        title: "Berhasil!",
        description: "Hasil polar telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin hasil polar",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Perkalian Bilangan Kompleks</CardTitle>
          <CardDescription>
            Kalikan bilangan kompleks dalam bentuk a + bi
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bilangan Kompleks Pertama (z₁)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bagian Real (a)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputValues.z1Real}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setInputValues(prev => ({ ...prev, z1Real: value }));
                          setZ1({ ...z1, real: value === '' ? 0 : parseInt(value, 10) });
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label>Bagian Imajiner (b)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputValues.z1Imaginary}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setInputValues(prev => ({ ...prev, z1Imaginary: value }));
                          setZ1({ ...z1, imaginary: value === '' ? 0 : parseInt(value, 10) });
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="text-center text-lg font-semibold">
                  z₁ = {formatComplex(z1)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bilangan Kompleks Kedua (z₂)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bagian Real (c)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputValues.z2Real}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setInputValues(prev => ({ ...prev, z2Real: value }));
                          setZ2({ ...z2, real: value === '' ? 0 : parseInt(value, 10) });
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label>Bagian Imajiner (d)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputValues.z2Imaginary}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setInputValues(prev => ({ ...prev, z2Imaginary: value }));
                          setZ2({ ...z2, imaginary: value === '' ? 0 : parseInt(value, 10) });
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="text-center text-lg font-semibold">
                  z₂ = {formatComplex(z2)}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button onClick={calculate} size="lg">
              Kalikan z₁ × z₂
            </Button>
          </div>

          {result && (
            <Tabs defaultValue="rectangular" className="mt-6">
              <div className="overflow-x-auto pb-1">
                <TabsList className="grid w-full grid-cols-2 min-w-[280px]">
                  <TabsTrigger 
                    value="rectangular" 
                    className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500 whitespace-nowrap"
                  >
                    Bentuk Persegi Panjang
                  </TabsTrigger>
                  <TabsTrigger 
                    value="polar" 
                    className="text-xs sm:text-sm px-2 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-blue-500 whitespace-nowrap"
                  >
                    Bentuk Polar
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="rectangular" className="space-y-4">
                <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
                  <CardHeader>
                    <CardTitle>Hasil Perkalian</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center text-2xl font-bold">
                      z₁ × z₂ = {formatComplex(result)}
                    </div>
                  </CardContent>
                </Card>

                {showSteps && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle>Langkah-langkah Perhitungan</CardTitle>
                        <Button
                          onClick={copyResult}
                          variant="outline"
                          size="sm"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Salin
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="font-mono text-sm space-y-2">
                        <div>z₁ × z₂ = {formatComplex(z1, true)} × {formatComplex(z2, true)}</div>
                        <div>= ({z1.real} + {z1.imaginary}i) × ({z2.real} + {z2.imaginary}i)</div>
                        <div>= {z1.real} × {z2.real} + {z1.real} × {z2.imaginary}i + {z1.imaginary}i × {z2.real} + {z1.imaginary}i × {z2.imaginary}i</div>
                        <div>= {z1.real * z2.real} + {z1.real * z2.imaginary}i + {z1.imaginary * z2.real}i + {z1.imaginary * z2.imaginary}i²</div>
                        <div>= {z1.real * z2.real} + {z1.real * z2.imaginary}i + {z1.imaginary * z2.real}i + {z1.imaginary * z2.imaginary}(-1)</div>
                        <div>= {z1.real * z2.real} + {z1.real * z2.imaginary}i + {z1.imaginary * z2.real}i - {z1.imaginary * z2.imaginary}</div>
                        <div>= ({z1.real * z2.real} - {z1.imaginary * z2.imaginary}) + ({z1.real * z2.imaginary} + {z1.imaginary * z2.real})i</div>
                        <div className="text-primary font-bold">= {result.real} + {result.imaginary}i</div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="polar" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">z₁ dalam Polar</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div>r₁ = {toPolar(z1).magnitude.toFixed(3)}</div>
                        <div>θ₁ = {toPolar(z1).angleDegrees.toFixed(1)}°</div>
                        <div className="font-semibold">
                          z₁ = {toPolar(z1).magnitude.toFixed(3)} ∠ {toPolar(z1).angleDegrees.toFixed(1)}°
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">z₂ dalam Polar</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div>r₂ = {toPolar(z2).magnitude.toFixed(3)}</div>
                        <div>θ₂ = {toPolar(z2).angleDegrees.toFixed(1)}°</div>
                        <div className="font-semibold">
                          z₂ = {toPolar(z2).magnitude.toFixed(3)} ∠ {toPolar(z2).angleDegrees.toFixed(1)}°
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {polarResult && (
                    <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between text-lg">
                          Hasil Polar
                          <Button
                            onClick={copyPolarResult}
                            variant="outline"
                            size="sm"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Salin
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div>r = r₁ × r₂ = {polarResult.magnitude.toFixed(3)}</div>
                          <div>θ = θ₁ + θ₂ = {polarResult.angleDegrees.toFixed(1)}°</div>
                          <div className="font-semibold text-primary">
                            z₁ × z₂ = {polarResult.magnitude.toFixed(3)} ∠ {polarResult.angleDegrees.toFixed(1)}°
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Rumus Perkalian Polar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm font-mono">
                      <div>Jika z₁ = r₁∠θ₁ dan z₂ = r₂∠θ₂, maka:</div>
                      <div className="text-primary font-bold">z₁ × z₂ = (r₁ × r₂) ∠ (θ₁ + θ₂)</div>
                      <div>Magnitude: kalikan magnitudo</div>
                      <div>Angle: jumlahkan sudut</div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplexMultiplication;